import React, { useState, useEffect, useMemo, useCallback } from "react";
import Navbar from "./Navbar";
import { useNavigate } from "react-router-dom";
import HelpMessage from "./HelpMessage";
import axios from "axios";
import PopUp from "./PopUp";
import { toast } from "react-toastify";

const Results = () => {
  const navigate = useNavigate();

  const monthDataString = localStorage.getItem("currentCard");
  const parsedMonthData = monthDataString ? JSON.parse(monthDataString) : null;
  const [summaryData, setSummaryData] = useState(parsedMonthData);

  const [isLoadingData, setIsLoadingData] = useState(true);
  const [detailsData, setDetailsData] = useState(null);
  const [reportSettings, setReportSettings] = useState(null);
  const [displayedColumns, setDisplayedColumns] = useState({});
  const [testMapping, setTestMapping] = useState([]);
  const [hasStatement, setHasStatement] = useState(false);

  const getMonthName = (monthNumber) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[monthNumber - 1] || "";
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!parsedMonthData || !parsedMonthData.month || !parsedMonthData.year) {
        setIsLoadingData(false);
        toast.error("Missing month selection. Redirecting to home...");
        setTimeout(() => navigate("/Home"), 1500);
        return;
      }

      // Declare variables outside try block so they're available in catch
      const token = localStorage.getItem("token");
      const role = localStorage.getItem("role") || "";
      const isAdmin = role.toLowerCase().includes("admin");
      
      // Get supplier from parsedMonthData or fall back to currentUser
      const rawSupplierCode = parsedMonthData.supplier || localStorage.getItem("currentUser")?.trim();
      const supplierCode = rawSupplierCode ? rawSupplierCode.replace(/\s/g, "") : null;
      
      let endpoint = `${process.env.REACT_APP_API_BASEURL}/api/Home/data/details/${parsedMonthData.month}-${parsedMonthData.year}`;

      if (isAdmin && supplierCode) {
        endpoint = `${process.env.REACT_APP_API_BASEURL}/api/Home/supplierdata/${supplierCode}/details/${parsedMonthData.month}-${parsedMonthData.year}`;
      }

      try {

        const { data } = await axios.get(endpoint, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const recordsets = data.resultProcedure?.recordsets || [];
        const hasDayOrVolume = (row = {}) => {
          if (!row || typeof row !== "object") return false;
          const candidates = [
            "Day",
            "day",
            "DAY",
            "dayT",
            "Collected",
            "collected",
            "CollectedVol",
            "COLLECTEDVOL",
          ];
          return candidates.some((key) => row[key] !== undefined && row[key] !== null);
        };

        const detailedRecordset =
          recordsets.find((set) => Array.isArray(set) && set.some((row) => hasDayOrVolume(row))) ||
          (recordsets.length > 1 ? recordsets[1] : recordsets[0]) ||
          [];

        const summaryRecord = data.resultProcedure?.recordsets?.[0]?.[0] || null;
        if (summaryRecord) {
          setSummaryData(summaryRecord);
          localStorage.setItem("currentCard", JSON.stringify(summaryRecord));
        }
        const detailRows = data.resultProcedure?.recordsets?.[1] || [];
        console.log('Detail rows received:', detailRows.length, detailRows);
        setDetailsData(detailRows);
        const mapping = data.resultProcedure?.testMapping || [];
        console.log('testMapping', mapping);
        setTestMapping(mapping);
        const normalizedSettings = (() => {
          const settings = data.reportSettings || null;
          if (!settings || mapping.length === 0) {
            return settings;
          }

          const lookup = new Map();
          mapping.forEach(({ key, code }) => {
            if (key) {
              lookup.set(key.trim().toLowerCase(), code);
            }
            if (code) {
              lookup.set(code.trim().toLowerCase(), code);
            }
          });

          const normalized = {};
          Object.entries(settings).forEach(([settingKey, value]) => {
            if (!settingKey) {
              return;
            }
            const mappedCode = lookup.get(settingKey.trim().toLowerCase());
            if (mappedCode) {
              normalized[mappedCode.toUpperCase()] = value;
            } else {
              normalized[settingKey.toUpperCase()] = value;
            }
          });
          return normalized;
        })();

        setReportSettings(normalizedSettings);
        setTestMapping(mapping);
        
        // Initialize displayedColumns - default ALL to true (all columns shown by default)
        // Build columns list from testMapping or defaults
        const allColumns = (() => {
          const mappedCols = (mapping || [])
            .filter(({ code }) => !!code)
            .map(({ code, label }) => ({
              key: code.toUpperCase(),
              label: (label || code)
            }));

          if (mappedCols.length > 0) {
            return mappedCols;
          }

          // Default columns if no mapping
          return [
            { key: "BF", label: "Butterfat" },
            { key: "SCC", label: "SCC" },
            { key: "PRT", label: "Protein" },
            { key: "LAC", label: "Lactose" },
            { key: "TBC", label: "TBC" },
            { key: "WAT", label: "Water%" },
            { key: "ANT", label: "Antibiotics" },
            { key: "THM", label: "Thermoduric" },
            { key: "TCM", label: "TCM" },
            { key: "UREA", label: "Urea" },
            { key: "RANT", label: "Routine Antibiotics" },
          ];
        })();
        
        const initial = {};
        
        // Set all columns to true by default (all shown)
        allColumns.forEach((col) => {
          const lowerKey = col.key.toLowerCase();
          if (lowerKey !== 'day' && lowerKey !== 'collected' && lowerKey !== 'collectedvol') {
            initial[lowerKey] = true;
          }
        });
        
        // Then override with any settings from reportSettings if they exist
        if (normalizedSettings && Object.keys(normalizedSettings).length > 0) {
          Object.keys(normalizedSettings).forEach((key) => {
            const setting = normalizedSettings[key];
            const isEnabled = setting && typeof setting === 'object' 
              ? setting.enabled !== false 
              : Boolean(setting);
            initial[key.toLowerCase()] = isEnabled;
          });
        } else if (data.reportSettings) {
          Object.keys(data.reportSettings).forEach((key) => {
            const setting = data.reportSettings[key];
            const isEnabled = setting && typeof setting === 'object' 
              ? setting.enabled !== false 
              : Boolean(setting);
            initial[key.toLowerCase()] = isEnabled;
          });
        }
        
        setDisplayedColumns(initial);

        setIsLoadingData(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        const errorMessage = error.response?.data?.message || error.message || "Unable to load results";
        console.error("Error details:", {
          endpoint: isAdmin && supplierCode 
            ? `${process.env.REACT_APP_API_BASEURL}/api/Home/supplierdata/${supplierCode}/details/${parsedMonthData.month}-${parsedMonthData.year}`
            : `${process.env.REACT_APP_API_BASEURL}/api/Home/data/details/${parsedMonthData.month}-${parsedMonthData.year}`,
          status: error.response?.status,
          supplierCode,
          isAdmin,
          month: parsedMonthData.month,
          year: parsedMonthData.year
        });
        toast.error(errorMessage);
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if a statement exists for the current month
  // This check fails silently - if it fails, the button just won't appear
  useEffect(() => {
    const checkStatementExists = async () => {
      if (!parsedMonthData || !parsedMonthData.month || !parsedMonthData.year) {
        setHasStatement(false);
        return;
      }

      try {
        const token = localStorage.getItem("token");
        
        // Get supplier from parsedMonthData or fall back to currentUser
        const rawSupplierCode = parsedMonthData.supplier || localStorage.getItem("currentUser")?.trim();
        const supplierId = rawSupplierCode ? rawSupplierCode.replace(/\s/g, "") : "";
        
        if (!supplierId) {
          setHasStatement(false);
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_BASEURL}/statements/${supplierId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const statementsByYear = response.data?.statements || {};
        const year = String(parsedMonthData.year);
        const month = String(parsedMonthData.month).padStart(2, "0");
        
        // Check if statement exists for this month/year
        const hasStatementForMonth = 
          statementsByYear[year] && 
          statementsByYear[year][month] && 
          statementsByYear[year][month].length > 0;
        
        setHasStatement(hasStatementForMonth);
      } catch (error) {
        // Fail silently - don't show error toasts for statement check
        // Just log to console for debugging
        console.debug("Could not check statement availability:", error.response?.status || error.message);
        setHasStatement(false);
      }
    };

    checkStatementExists();
  }, [parsedMonthData]);

  const mappingLabelLookup = useMemo(() => {
    const map = new Map();
    (testMapping || []).forEach(({ code, label }) => {
      if (!code) return;
      const normalizedCode = code.toUpperCase();
      map.set(normalizedCode, label || code);
      map.set(code.toLowerCase(), label || code);
    });
    return map;
  }, [testMapping]);

  const summaryMapping = useMemo(() => {
    const map = new Map();
    (testMapping || []).forEach(({ key, code, label }) => {
      const resolvedLabel = label || code || key;
      if (code) {
        map.set(code.toUpperCase(), resolvedLabel);
        map.set(code.toLowerCase(), resolvedLabel);
      }
      if (key) {
        map.set(key.toUpperCase(), resolvedLabel);
        map.set(key.toLowerCase(), resolvedLabel);
      }
    });
    return map;
  }, [testMapping]);

  const isColumnEnabled = (columnKey) => {
    if (!reportSettings || Object.keys(reportSettings).length === 0) {
      return true;
    }

    const keyUpper = columnKey.toUpperCase();
    const keyLower = columnKey.toLowerCase();
    const entry = reportSettings[keyUpper] ?? reportSettings[keyLower];

    if (entry === undefined) {
      return true;
    }

    if (typeof entry === "object" && entry !== null) {
      if (entry.enabled === undefined) {
        return true;
      }
      return Boolean(entry.enabled);
    }

    return Boolean(entry);
  };

  // Helper function to convert to sentence case (first letter uppercase, rest lowercase)
  const toSentenceCase = (str) => {
    if (!str || typeof str !== "string") return str;
    if (str.length === 0) return str;
    
    // Handle special acronyms that should stay uppercase (SCC, TBC, TCM, BAC, BSE, FPD, etc.)
    const acronyms = ["SCC", "TBC", "TCM", "BAC", "BSE", "FPD", "BCFU", "SBCFU"];
    const upperStr = str.toUpperCase();
    if (acronyms.includes(upperStr) && str.length <= 5) {
      return upperStr;
    }
    
    // Handle cases like "Water%" - keep the % at the end
    if (str.includes("%")) {
      const parts = str.split("%");
      return parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase() + "%" + (parts[1] || "");
    }
    
    // Convert to sentence case: first letter uppercase, rest lowercase
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getColumnLabel = (columnKey) => {
    if (!columnKey) return "";
    const normalized = typeof columnKey === "string" ? columnKey.toUpperCase() : columnKey;
    let label = "";
    
    if (summaryMapping.has(normalized)) {
      label = summaryMapping.get(normalized);
    } else if (!reportSettings) {
      label = columnKey === "day" ? "Day" : columnKey.toUpperCase();
    } else {
      const upperKey = columnKey.toUpperCase();
      if (reportSettings[upperKey]?.label) {
        label = reportSettings[upperKey].label;
      } else {
        label = columnKey === "day" ? "Day" : upperKey;
      }
    }
    
    // Convert to sentence case
    return toSentenceCase(label);
  };

  const getDataKey = (columnKey) => {
    const map = {
      day: "dayT",
      collectedvol: "CollectedVol",
      collected: "CollectedVol",
      vol: "CollectedVol",
      bf: "BF",
      prt: "PRT",
      scc: "SCC",
      bac: "BAC",
      bse: "BSE",
      fpd: "FPD",
      ant: "ANT",
      urea: "UREA",
      syn: "SYN",
      bcfu: "BCFU",
      sbcfu: "SBCFU",
    };
    return map[columnKey.toLowerCase()] || columnKey.toUpperCase();
  };

  const getDisplayColumns = useCallback(() => {
    const mappedColumns = (testMapping || [])
      .filter(({ code }) => !!code)
      .map(({ code, label }) => ({
        key: code.toUpperCase(),
        label: (label || code)
      }));

    if (mappedColumns.length > 0) {
      return mappedColumns.filter(({ key }) => isColumnEnabled(key));
    }

    if (!reportSettings) {
      return [
        { key: "BF", label: "Butterfat" },
        { key: "SCC", label: "SCC" },
        { key: "PRT", label: "Protein" },
        { key: "BAC", label: "BAC" },
        { key: "BSE", label: "BSE" },
        { key: "FPD", label: "FPD" },
        { key: "ANT", label: "Antibiotics" },
        { key: "UREA", label: "Urea" },
        { key: "BCFU", label: "BCFU" },
        { key: "SBCFU", label: "SBCFU" },
        { key: "LAC", label: "Lactose" },
        { key: "TBC", label: "TBC" },
        { key: "WAT", label: "Water%" },
        { key: "THM", label: "Thermoduric" },
        { key: "TCM", label: "TCM" },
        { key: "RANT", label: "Routine Antibiotics" },
      ];
    }

    return Object.keys(reportSettings)
      .filter((key) => isColumnEnabled(key))
      .map((key) => {
        const setting = reportSettings[key];
        const label = setting && typeof setting === 'object' && setting.label 
          ? setting.label 
          : key.toUpperCase();
        return { key: key.toUpperCase(), label };
      });
  }, [testMapping, reportSettings]);

  const [popupStyle, setPopupStyle] = useState({
    visibility: "hidden",
    opacity: 0,
  });

  const handleFilterApply = (updatedFilters) => {
    setDisplayedColumns(updatedFilters);
    setPopupStyle({ visibility: "hidden", opacity: 0 });
  };

  const handleNotesClick = () => {
    if (parsedMonthData) {
      navigate(`/Field-Notes/${parsedMonthData.month}-${parsedMonthData.year}`);
    }
  };

  const handleScreenOptionsClick = () => {
    setPopupStyle({ visibility: "visible", opacity: 1 });
  };

  const resultsColumns = useMemo(() => {
    const columns = getDisplayColumns();
    const baseColumns = [
      { key: "Day", label: "Day" },
      { key: "Collected", label: "Collected" }
    ];

    const hasCustomSelection = displayedColumns && Object.keys(displayedColumns).length > 0;

    const metrics = columns.filter((col) => {
      const lower = typeof col.key === "string" ? col.key.toLowerCase() : col.key;
      if (lower === "day" || lower === "collected" || lower === "collectedvol" || lower === "vol") {
        return false;
      }
      if (!hasCustomSelection) {
        return true;
      }
      // Filter based on displayedColumns - only show columns that are true
      return displayedColumns[lower] === true;
    });

    return [...baseColumns, ...metrics];
  }, [getDisplayColumns, displayedColumns]);

  const parsePaymentValue = (value) => {
    if (typeof value === "number") {
      return value;
    }
    if (typeof value === "string") {
      const numeric = Number(value.replace(/[^0-9.-]+/g, ""));
      return Number.isNaN(numeric) ? 0 : numeric;
    }
    return 0;
  };

  const formatCurrency = useCallback((value) => {
    const numeric = Number(value);
    if (Number.isNaN(numeric) || numeric === 0) {
      return "—";
    }
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "EUR"
    }).format(numeric);
  }, []);

  const paymentRaw =
    summaryData?.Payment ??
    summaryData?.payment ??
    parsedMonthData?.Payment ??
    parsedMonthData?.payment;
  const paymentAmount = parsePaymentValue(paymentRaw);
  const hasPayment = paymentAmount > 0;
  const formattedPayment = hasPayment ? formatCurrency(paymentAmount) : null;

  const latestMonthLabel = summaryData
    ? `${getMonthName(summaryData.month)} ${summaryData.year}`
    : parsedMonthData
    ? `${getMonthName(parsedMonthData.month)} ${parsedMonthData.year}`
    : "N/A";

  const formatSummaryValue = (key, rawValue) => {
    if (rawValue === null || rawValue === undefined || rawValue === "") {
      return "-";
    }

    const numeric = Number(rawValue);
    if (!Number.isNaN(numeric)) {
      if (key.toLowerCase().includes("vol")) {
        return numeric.toLocaleString();
      }
      return numeric.toFixed(2);
    }

    return String(rawValue).trim() || "-";
  };

  const summaryRows = useMemo(() => {
    if (!summaryData) return [];
    if (testMapping && testMapping.length > 0) {
      const metricsOnlyMapping = testMapping.filter(({ code }) => code && code.toLowerCase() !== 'collected');
      const hasAnyValue = metricsOnlyMapping.some(({ code }) => summaryData[code] !== undefined && summaryData[code] !== null);
      if (!hasAnyValue && detailsData && detailsData.length > 0) {
        const detailSummary = detailsData.reduce((acc, row) => {
          metricsOnlyMapping.forEach(({ code }) => {
            const value = row[code];
            if (value !== null && value !== undefined) {
              if (!acc[code]) {
                acc[code] = [];
              }
              acc[code].push(Number(value));
            }
          });
          return acc;
        }, {});

        const summaryPairs = [];
        for (let i = 0; i < metricsOnlyMapping.length; i += 2) {
          const left = metricsOnlyMapping[i];
          const right = metricsOnlyMapping[i + 1];

          const buildCell = (column) => {
            if (!column) return null;
            const values = detailSummary[column.code] || [];
            const avg = values.length
              ? Number((values.reduce((acc, value) => acc + value, 0) / values.length).toFixed(2))
              : "-";
            return {
              label: summaryMapping.get(column.code.toUpperCase()) || column.code,
              value: avg
            };
          };

          summaryPairs.push({
            left: buildCell(left),
            right: buildCell(right)
          });
        }

        return summaryPairs;
      }
    }

    const columns = [
      { key: "Collected", label: "Collected" },
      ...getDisplayColumns(),
    ];
    const rows = [];
    // Create rows with 4 columns (2 label-value pairs per row)
    for (let i = 0; i < columns.length; i += 4) {
      const col1 = columns[i];
      const col2 = columns[i + 1];
      const col3 = columns[i + 2];
      const col4 = columns[i + 3];

      const buildCell = (column) => {
        if (!column) return null;
        const value =
          summaryData[column.key] ??
          summaryData[getDataKey(column.key)] ??
          summaryData[column.label?.toUpperCase?.()] ??
          summaryData[column.label?.toLowerCase?.()] ??
          null;
        return {
          label: getColumnLabel(column.key),
          value: formatSummaryValue(column.key, value),
        };
      };

      const row = {
        col1: buildCell(col1),
        col2: buildCell(col2),
        col3: buildCell(col3),
        col4: buildCell(col4),
      };
      rows.push(row);
    }
    return rows;
  }, [testMapping, detailsData, summaryMapping, getDisplayColumns, summaryData]);

  const displayRows = useMemo(() => {
    if (detailsData && detailsData.length > 0) {
      return detailsData;
    }

    return [];
  }, [detailsData]);

  const handleViewStatement = () => {
    if (!parsedMonthData) return;
    const month = String(parsedMonthData.month).padStart(2, "0");
    const year = String(parsedMonthData.year);
    localStorage.setItem("statementSelection", JSON.stringify({ year, month }));
    navigate("/Statement");
  };

  const renderMonthMetric = useCallback(
    (column, value) => {
      if (!column) return null;

      const label = getColumnLabel(column.key);
      let displayValue = value;

      if (value === null || value === undefined || value === "") {
        displayValue = "-";
      } else if (column.key.toLowerCase() === "collected" || column.key.toLowerCase() === "collectedvol") {
        const numeric = Number(value);
        displayValue = Number.isNaN(numeric) ? value : numeric.toLocaleString();
      } else if (typeof value === "number") {
        displayValue = value.toFixed(2);
      }

      return {
        key: column.key,
        label,
        value: displayValue
      };
    },
    [getColumnLabel]
  );

  // Don't render if we don't have the required month data
  if (!parsedMonthData || !parsedMonthData.month || !parsedMonthData.year) {
      return (
        <div className="customer-layout">
          <Navbar />
          <main className="customer-main results-layout">
            <section className="card">
              <header className="card-header">
                <button className="button ghost sm" onClick={() => navigate("/Home")}>
                  <i className="uicon-arrow-narrow-left" /> Back
                </button>
                <h1 className="card-title">Results</h1>
              </header>
              <div className="notes-empty">Missing month selection. Please select a month from the home page.</div>
            </section>
          </main>
        </div>
      );
  }

  return (
    <div className="customer-layout">
      <Navbar />
      <main className="customer-main results-layout">
        <section className="results-summary-card card">
          <header className="results-summary-card__header">
            <div className="results-summary-card__actions--left">
              <button className="button ghost sm" onClick={() => navigate("/Home")}>
                <i className="uicon-arrow-narrow-left" /> Back
              </button>
            </div>
            <div className="results-summary-card__title-block">
              <h1>Collections & Test Results</h1>
              <p>Month: {latestMonthLabel}</p>
            </div>
            <div className="results-summary-card__actions--right">
              <button className="button ghost sm" onClick={handleNotesClick}>
                <i className="uicon-message" /> Notes
              </button>
              {hasStatement && (
                <button className="button secondary sm" onClick={handleViewStatement}>
                  <i className="uicon-docs" /> View Statement
                </button>
              )}
            </div>
          </header>

          <div className="results-summary-table">
            <table>
              <tbody>
                {summaryRows.map((row, index) => (
                  <tr key={`summary-row-${index}`}>
                    {row.col1 ? (
                      <>
                        <th scope="row">{row.col1.label}</th>
                        <td>{row.col1.value}</td>
                      </>
                    ) : (
                      <>
                        <th></th>
                        <td></td>
                      </>
                    )}
                    {row.col2 ? (
                      <>
                        <th scope="row">{row.col2.label}</th>
                        <td>{row.col2.value}</td>
                      </>
                    ) : (
                      <>
                        <th></th>
                        <td></td>
                      </>
                    )}
                    {row.col3 ? (
                      <>
                        <th scope="row">{row.col3.label}</th>
                        <td>{row.col3.value}</td>
                      </>
                    ) : (
                      <>
                        <th></th>
                        <td></td>
                      </>
                    )}
                    {row.col4 ? (
                      <>
                        <th scope="row">{row.col4.label}</th>
                        <td>{row.col4.value}</td>
                      </>
                    ) : (
                      <>
                        <th></th>
                        <td></td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasPayment && formattedPayment && (
            <footer className="results-summary-card__footer">
              <span>Payment</span>
              <strong>{formattedPayment}</strong>
            </footer>
          )}
        </section>

        <section className="results-table card">
          <header className="results-table__header">
            <h2>Daily Breakdown</h2>
            <button className="button ghost sm" onClick={handleScreenOptionsClick}>
              <i className="uicon-filters" /> Display Options
            </button>
          </header>
          <div className="results-table__wrapper">
            <table>
              <thead>
                <tr>
                  {resultsColumns.map((col) => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoadingData ? (
                  <tr>
                    <td colSpan={resultsColumns.length}>Loading data…</td>
                  </tr>
                ) : displayRows && displayRows.length > 0 ? (
                  displayRows.map((dayRow, rowIndex) => (
                    <tr key={rowIndex}>
                      {resultsColumns.map((col) => {
                        const lowerKey = typeof col.key === "string" ? col.key.toLowerCase() : col.key;
                        const lowerLabel = typeof col.label === "string" ? col.label.toLowerCase() : col.label;
                        const candidates = [col.key, getDataKey(col.key), lowerKey, col.label, lowerLabel];

                        if (lowerKey === "collectedvol" || lowerKey === "collected" || lowerLabel === "collected") {
                          candidates.push("Collected", "CollectedVol", "collected");
                        }

                        if (lowerKey === "day" || lowerLabel === "day") {
                          candidates.push("day", "Day", "dayT");
                        }

                        let rawValue = null;
                        for (const keyVariant of candidates) {
                          if (!keyVariant && keyVariant !== 0) continue;
                          if (dayRow[keyVariant] !== undefined && dayRow[keyVariant] !== null) {
                            rawValue = dayRow[keyVariant];
                            break;
                          }
                        }

                        let displayValue = rawValue ?? "-";

                        if (lowerKey === "day") {
                          displayValue = rawValue ?? "-";
                        } else if (lowerKey === "collected" || lowerKey === "collectedvol" || lowerLabel === "collected") {
                          const numeric = Number(rawValue);
                          displayValue = Number.isNaN(numeric) ? rawValue ?? "-" : numeric.toLocaleString();
                        } else if (typeof rawValue === "number") {
                          displayValue = rawValue.toFixed(2);
                        }
                        
                        return (
                          <td key={col.key} data-label={col.label}>
                            {displayValue}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                   <tr>
                     <td colSpan={resultsColumns.length}>No daily data captured yet.</td>
                   </tr>
                 )}
               </tbody>
            </table>
          </div>
        </section>

        <PopUp
          handleFilterApply={handleFilterApply}
          popupStyle={popupStyle}
          columns={getDisplayColumns()}
          displayedColumns={displayedColumns}
          onClose={() => setPopupStyle({ visibility: "hidden", opacity: 0 })}
        />

        <HelpMessage />
      </main>
    </div>
  );
};

export default Results;